import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import toast from 'react-hot-toast';
import { weeklyPlanApi, settingsApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { PriorityBadge, TeamBadge, OverdueBadge } from '../components/ui/Badge';
import { formatAssigneeNames, getOverdueDays, TEAMS } from '../lib/utils';

type Status = 'Pending' | 'In Progress' | 'Done' | 'Blocked';

const COLUMNS: { id: Status; label: string; color: string; headerBg: string; dot: string }[] = [
  { id: 'Pending', label: 'To Do', color: 'border-gray-200', headerBg: 'bg-gray-50', dot: 'bg-gray-400' },
  { id: 'In Progress', label: 'In Progress', color: 'border-blue-200', headerBg: 'bg-blue-50', dot: 'bg-blue-500' },
  { id: 'Done', label: 'Done', color: 'border-green-200', headerBg: 'bg-green-50', dot: 'bg-green-500' },
  { id: 'Blocked', label: 'Blocked', color: 'border-red-200', headerBg: 'bg-red-50', dot: 'bg-red-500' },
];

function TaskCard({ task, filterMember, isDragging = false }: { task: any; filterMember?: string; isDragging?: boolean }) {
  const isFollowUp = filterMember &&
    task.follow_up_by?.toLowerCase() === filterMember.toLowerCase() &&
    task.assigned_to?.toLowerCase() !== filterMember.toLowerCase();
  const overdueDays = getOverdueDays(task.due_date, task.status);

  return (
    <div className={`bg-white rounded-lg border ${overdueDays ? 'border-red-200' : 'border-gray-200'} p-3 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-all ${isDragging ? 'opacity-40' : ''}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-gray-800 leading-tight line-clamp-2">{task.name}</p>
        <PriorityBadge priority={task.priority} />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {task.assigned_team && <TeamBadge team={task.assigned_team} />}
        {formatAssigneeNames(task) && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{formatAssigneeNames(task)}</span>
        )}
        {isFollowUp && (
          <span className="text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
            Follow-up
          </span>
        )}
        {overdueDays && <OverdueBadge days={overdueDays} />}
        <span className="text-xs text-gray-400 ml-auto capitalize">{task.source}</span>
      </div>
      {task.follow_up_by && !isFollowUp && task.follow_up_by?.toLowerCase() !== task.assigned_to?.toLowerCase() && (
        <p className="text-xs text-gray-400 mt-1.5">Follow-up: {task.follow_up_by}</p>
      )}
      {task.due_date && (
        <p className="text-xs text-gray-400 mt-1.5">Due: {task.due_date}</p>
      )}
      {task.status === 'Done' && task.completed_at && (
        <p className="text-xs text-green-500 mt-1.5">
          ✓ {new Date(new Date(task.completed_at).getTime() + 5 * 60 * 60 * 1000).toLocaleString('en-PK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </p>
      )}
    </div>
  );
}

function DraggableCard({ task, filterMember }: { task: any; filterMember?: string }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${task.source}-${task.id}`,
    data: task,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 999 : undefined,
    position: isDragging ? 'fixed' as const : undefined,
  } : undefined;

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <TaskCard task={task} filterMember={filterMember} isDragging={isDragging} />
    </div>
  );
}

function DroppableColumn({ column, tasks, filterDate, filterMember }: { column: typeof COLUMNS[0]; tasks: any[]; filterDate?: string; filterMember?: string }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const isDoneCol = column.id === 'Done';

  return (
    <div className={`flex flex-col flex-1 min-w-60 rounded-xl border-2 ${column.color} overflow-hidden ${isOver ? 'ring-2 ring-primary-400' : ''}`}>
      {/* Header */}
      <div className={`${column.headerBg} px-4 py-3 flex items-center gap-2 border-b ${column.color}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${column.dot}`} />
        <span className="font-semibold text-sm text-gray-700">{column.label}</span>
        {isDoneCol && filterDate && (
          <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-0.5 rounded-full">
            {filterDate}
          </span>
        )}
        <span className="ml-auto text-xs font-bold text-gray-500 bg-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
          {tasks.length}
        </span>
      </div>
      {isDoneCol && filterDate && filterMember && (
        <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-xs text-green-700 font-medium">
          {filterMember} completed {tasks.length} task{tasks.length !== 1 ? 's' : ''} on {new Date(filterDate + 'T00:00:00').toLocaleDateString('en-PK', { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
      )}

      {/* Drop zone */}
      <div ref={setNodeRef} className="flex-1 p-3 space-y-2 min-h-[200px] overflow-y-auto">
        {tasks.map(task => (
          <DraggableCard key={`${task.source}-${task.id}`} task={task} filterMember={filterMember} />
        ))}
        {tasks.length === 0 && (
          <div className={`h-20 rounded-lg border-2 border-dashed ${column.color} flex items-center justify-center text-sm text-gray-400`}>
            Drop here
          </div>
        )}
      </div>
    </div>
  );
}

export default function WeeklyPlan() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isMember = (user as any)?.role === 'member';
  const [filterMember, setFilterMember] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const todayPKT = new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [filterDate, setFilterDate] = useState(todayPKT);
  const [activeTask, setActiveTask] = useState<any>(null);

  const { data: membersData } = useQuery({
    queryKey: ['settings', 'team-members'],
    queryFn: settingsApi.teamMembers.list,
  });

  const params: Record<string, string | undefined> = {};
  if (filterMember) params.assigned_to = filterMember;
  if (filterTeam) params.team = filterTeam;
  if (filterDate) params.completed_date = filterDate;

  const { data, isLoading } = useQuery({
    queryKey: ['weekly-plan', params],
    queryFn: () => weeklyPlanApi.list(params),
  });

  const statusMutation = useMutation({
    mutationFn: ({ source, id, status }: { source: string; id: number; status: string }) =>
      weeklyPlanApi.updateStatus(source, id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['weekly-plan'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => toast.error('Failed to update status'),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const grouped = data?.data || {
    'Pending': [],
    'In Progress': [],
    'Done': [],
    'Blocked': [],
  };

  const members = membersData?.data || [];
  const visibleGrouped = grouped;

  function handleDragStart(event: DragStartEvent) {
    setActiveTask(event.active.data.current);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;

    const newStatus = over.id as Status;
    const task = active.data.current as any;

    if (task.status === newStatus) return;

    // Optimistic update
    qc.setQueryData(['weekly-plan', params], (old: any) => {
      if (!old) return old;
      const updated = { ...old.data };
      // Remove from old column
      for (const key of Object.keys(updated)) {
        updated[key] = updated[key].filter((t: any) =>
          !(t.id === task.id && t.source === task.source)
        );
      }
      // Add to new column
      updated[newStatus] = [...(updated[newStatus] || []), { ...task, status: newStatus }];
      return { ...old, data: updated };
    });

    statusMutation.mutate({ source: task.source, id: task.id, status: newStatus });
  }

  const totalTasks = Object.values(visibleGrouped).flat().length;

  return (
    <div className="space-y-5 h-full flex flex-col">
      {/* Controls */}
      <div className="flex items-center gap-4 bg-white rounded-xl px-5 py-3 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Team:</label>
          <select
            value={filterTeam}
            onChange={e => { setFilterTeam(e.target.value); setFilterMember(''); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Teams</option>
            {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Member:</label>
          <select
            value={filterMember}
            onChange={e => setFilterMember(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Members</option>
            {members.map((m: any) => (
              <option key={m.id} value={m.name}>{m.name} ({m.team})</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Done Date:</label>
          <input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {filterDate && (
            <button onClick={() => setFilterDate('')} className="text-gray-400 hover:text-gray-600" title="Clear date">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          )}
        </div>
        {(filterMember || filterTeam || filterDate) && (
          <button
            onClick={() => { setFilterMember(''); setFilterTeam(''); setFilterDate(''); }}
            className="text-sm text-red-500 hover:text-red-700"
          >
            Clear all
          </button>
        )}
        <span className="ml-auto text-sm text-gray-400">{totalTasks} tasks</span>
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex gap-4 flex-1 overflow-x-auto pb-4">
            {COLUMNS.map(column => (
              <DroppableColumn
                key={column.id}
                column={column}
                tasks={visibleGrouped[column.id] || []}
                filterDate={filterDate}
                filterMember={filterMember}
              />
            ))}
          </div>

          <DragOverlay>
            {activeTask && (
              <div className="shadow-2xl rotate-2 scale-105">
                <TaskCard task={activeTask} filterMember={filterMember} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
